import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Alert,
  Grid,
  Fade,
  Container,
  Divider,
  useTheme,
  alpha,
  lighten,
} from "@mui/material";
import {
  Send,
  Description,
  ViewList,
  CheckCircle,
  CalendarToday,
  Person,
  Message,
} from "@mui/icons-material";
import axios from "axios";
import { baseURL, config, requestID } from "../../../common/utils";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router";

const Recommendation: React.FC = () => {
  const today = new Date().toISOString().split("T")[0];
  const history = useHistory();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [formData, setFormData] = useState({
    date: today,
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = new FormData();
      payload.append("requestID", requestID());
      payload.append("message", formData.message);

      const response = await axios.post(
        `${baseURL}/Onboardings/requestRecommendation`,
        payload,
        config
      );
      
      setSuccess("Recommendation request submitted successfully!");
      setFormData({ message: "", date: today });
    } catch (err) {
      console.error(err);
      setError("Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonPage className="pageDown">
      <IonContent>
        {/* Light Themed Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${lighten(theme.palette.primary.light, 0.9)} 0%, ${lighten(theme.palette.background.paper, 0.2)} 100%)`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            py: 3,
            mb: 4,
            boxShadow: `0 2px 12px ${alpha(theme.palette.primary.light, 0.1)}`,
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                    mr: 3,
                    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.light, 0.2)}`,
                  }}
                >
                  <Description 
                    sx={{ 
                      color: theme.palette.primary.main,
                      fontSize: 28 
                    }} 
                  />
                </Box>
                <Box>
                  <Typography 
                    variant="h4" 
                    fontWeight="600"
                    sx={{
                      background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      color: 'transparent',
                    }}
                  >
                    Recommendation Request
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: alpha(theme.palette.text.primary, 0.7),
                      mt: 0.5 
                    }}
                  >
                    Submit a professional recommendation request
                  </Typography>
                </Box>
              </Box>
              
              <Button
                variant="contained"
                onClick={() => history.push('/folder/view-recommendations')}
                startIcon={<ViewList />}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  fontWeight: "600",
                  fontSize: '0.95rem',
                  background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                  '&:hover': {
                    boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transform: 'translateY(-1px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                View History
              </Button>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="xl" sx={{ mb: 6 }}>
          <Grid container spacing={4}>
            {/* Main Form Section */}
            <Grid item xs={12} lg={8}>
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${lighten(theme.palette.background.paper, 0.1)} 0%, ${theme.palette.background.paper} 100%)`,
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.light, 0.08)}`,
                  backdropFilter: 'blur(10px)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                  }
                }}
              >
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        disabled
                        label="Request Date"
                        name="date"
                        value={new Date(formData.date).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })}
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <CalendarToday 
                              sx={{ 
                                mr: 2, 
                                color: alpha(theme.palette.text.secondary, 0.7) 
                              }} 
                            />
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.light, 0.05),
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        disabled
                        label="Request ID"
                        value={requestID()}
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <Person 
                              sx={{ 
                                mr: 2, 
                                color: alpha(theme.palette.text.secondary, 0.7) 
                              }} 
                            />
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.light, 0.05),
                            },
                          },
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Recommendation Details"
                        name="message"
                        placeholder="Describe your professional background, specific skills, achievements, and the type of recommendation you require..."
                        multiline
                        rows={6}
                        margin="normal"
                        value={formData.message}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: (
                            <Message 
                              sx={{ 
                                mr: 2, 
                                mt: 2.2,
                                color: alpha(theme.palette.text.secondary, 0.7) 
                              }} 
                            />
                          )
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            transition: 'all 0.2s ease-in-out',
                            backgroundColor: alpha(theme.palette.background.default, 0.3),
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.primary.light, 0.05),
                              borderColor: theme.palette.primary.light,
                            },
                            '&.Mui-focused': {
                              backgroundColor: alpha(theme.palette.primary.light, 0.08),
                              borderColor: theme.palette.primary.main,
                              boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.1)}`,
                            },
                          },
                        }}
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          mt: 1, 
                          display: 'block',
                          color: alpha(theme.palette.text.secondary, 0.7) 
                        }}
                      >
                        {formData.message.length}/500 characters
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading || !formData.message.trim() || formData.message.length < 10}
                          startIcon={
                            loading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <Send />
                            )
                          }
                          sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: "600",
                            minWidth: 200,
                            background: `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            '&:hover': {
                              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                              boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                              transform: 'translateY(-1px)',
                            },
                            '&:disabled': {
                              background: alpha(theme.palette.action.disabled, 0.5),
                              boxShadow: 'none',
                            },
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          {loading ? "Submitting..." : "Submit Request"}
                        </Button>

                        <Button
                          variant="outlined"
                          size="large"
                          onClick={() => setFormData({ ...formData, message: "" })}
                          sx={{
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            fontWeight: "600",
                            borderColor: alpha(theme.palette.primary.light, 0.5),
                            color: theme.palette.primary.main,
                            '&:hover': {
                              borderColor: theme.palette.primary.main,
                              backgroundColor: alpha(theme.palette.primary.light, 0.1),
                              transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          Clear Form
                        </Button>
                      </Box>
                    </Grid>

                    {/* Status Messages */}
                    <Grid item xs={12}>
                      <Fade in={!!error || !!success}>
                        <Box>
                          {error && (
                            <Alert 
                              severity="error" 
                              sx={{ 
                                borderRadius: 2, 
                                mt: 2,
                                background: alpha(theme.palette.error.light, 0.1),
                                border: `1px solid ${alpha(theme.palette.error.light, 0.3)}`,
                              }}
                            >
                              {error}
                            </Alert>
                          )}
                          {success && (
                            <Alert 
                              severity="success" 
                              sx={{ 
                                borderRadius: 2, 
                                mt: 2,
                                background: alpha(theme.palette.success.light, 0.1),
                                border: `1px solid ${alpha(theme.palette.success.light, 0.3)}`,
                              }}
                            >
                              {success}
                            </Alert>
                          )}
                        </Box>
                      </Fade>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            {/* Sidebar Section */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Guidelines Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${lighten(theme.palette.background.paper, 0.1)} 0%, ${theme.palette.background.paper} 100%)`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.light, 0.08)}`,
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    gutterBottom
                    sx={{
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <CheckCircle sx={{ mr: 1.5, fontSize: 24 }} />
                    Submission Guidelines
                  </Typography>
                  <Divider 
                    sx={{ 
                      my: 2, 
                      background: `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.5)}, transparent)` 
                    }} 
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {[
                      "Be specific about your skills and achievements",
                      "Include relevant project details and timelines",
                      "Mention the purpose of the recommendation",
                      "Keep professional tone throughout",
                      "Provide context for your request"
                    ].map((guideline, index) => (
                      <Box 
                        key={index} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start',
                          p: 1.5,
                          borderRadius: 2,
                          background: alpha(theme.palette.primary.light, 0.05),
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            background: alpha(theme.palette.primary.light, 0.1),
                            transform: 'translateX(4px)',
                          }
                        }}
                      >
                        <CheckCircle sx={{ color: 'success.main', mr: 2, mt: 0.25, fontSize: 18 }} />
                        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                          {guideline}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>

                {/* Quick Info Card */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    border: `1px solid ${alpha(theme.palette.primary.light, 0.3)}`,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.05)} 100%)`,
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.light, 0.1)}`,
                  }}
                >
                  <Typography 
                    variant="h6" 
                    fontWeight="600" 
                    gutterBottom 
                    color="primary.main"
                  >
                    Quick Info
                  </Typography>
                  <Divider 
                    sx={{ 
                      my: 2, 
                      background: `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0.5)}, transparent)` 
                    }} 
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderRadius: 2,
                        background: alpha(theme.palette.background.paper, 0.7),
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">Response Time</Typography>
                      <Typography variant="body2" fontWeight="600" color="primary.main">
                        24-48 hours
                      </Typography>
                    </Box>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        p: 1.5,
                        borderRadius: 2,
                        background: alpha(theme.palette.background.paper, 0.7),
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Typography variant="body2" fontWeight="600" color="success.main">
                        Active
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </IonContent>
    </IonPage>
  );
};

export default Recommendation;